import React, { useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import ScheduleRequestModal from "./ScheduleRequestModal";
import {
  scheduledPublishDate,
  formatPositionLabel,
} from "../../utils/tenderFormatters";

/**
 * The action set a DRAFT request carries, in one place for the card view and
 * the list view.
 *
 * A live request offers "View pitches"; a draft has none, so it offers the
 * five things a plan can need instead:
 *
 *   Preview      the real preview the agents get (owned by the caller, which
 *                already mounts common/RequestPreviewModal)
 *   Post now     the single status switch draft -> active
 *   Schedule     opens the picker; the picker also removes an existing date
 *   Delete       real delete, behind the platform confirm dialog
 *   Edit         stays where it always was, in the card's kebab menu, and
 *                opens the existing wizard prefilled
 *
 * This component is presentational plus two dialogs. Every transition is
 * handed in from hooks/clubtender/useClubTenderActions.js, so the card, the
 * list row and the preview modal all fire the identical handler.
 *
 * Buttons are min-h-[44px] throughout, and the pair wraps instead of shrinking
 * below the tap size on a narrow phone.
 */
const RequestDraftActions = ({
  tender,
  transferWindow = null,
  isSaving = false,
  onPreview,
  onPostNow,
  onSchedule,
  onRemoveSchedule,
  onDelete,
  variant = "card",
}) => {
  const [showSchedule, setShowSchedule] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const scheduled = scheduledPublishDate(tender);
  const scheduleLabel = scheduled ? "Reschedule" : "Schedule";
  const title = formatPositionLabel(tender?.position) || "";

  const secondary =
    "min-h-[44px] flex-1 min-w-[132px] rounded-lg border border-[#E6E6E6] bg-white px-4 text-sm font-medium text-[#1E1E1E] hover:bg-gray-50 disabled:opacity-50";
  const primary =
    "min-h-[44px] flex-1 min-w-[132px] rounded-lg bg-[#2283FF] px-4 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50";
  const danger =
    "min-h-[44px] flex-1 min-w-[132px] rounded-lg border border-[#E6E6E6] bg-white px-4 text-sm font-medium text-[#FF4D4F] hover:bg-red-50 disabled:opacity-50";

  const stop = (fn) => (event) => {
    event.stopPropagation();
    fn();
  };

  return (
    <>
      <div
        className={
          variant === "row"
            ? "flex flex-wrap items-center gap-2"
            : "flex flex-col gap-2"
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {onPreview && (
            <button
              type="button"
              onClick={stop(onPreview)}
              className={secondary}
            >
              Preview
            </button>
          )}
          <button
            type="button"
            onClick={stop(onPostNow)}
            disabled={isSaving}
            className={primary}
          >
            {isSaving ? "Working..." : "Post now"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={stop(() => setShowSchedule(true))}
            disabled={isSaving}
            className={secondary}
          >
            {scheduleLabel}
          </button>
          <button
            type="button"
            onClick={stop(() => setShowDelete(true))}
            disabled={isSaving}
            className={danger}
          >
            Delete
          </button>
        </div>
      </div>

      <ScheduleRequestModal
        isOpen={showSchedule}
        onClose={() => setShowSchedule(false)}
        onConfirm={async (date) => {
          const ok = await onSchedule?.(date.toISOString());
          if (ok !== false) setShowSchedule(false);
        }}
        onRemove={async () => {
          const ok = await onRemoveSchedule?.();
          if (ok !== false) setShowSchedule(false);
        }}
        requestTitle={title}
        clubName={tender?.club?.name || ""}
        currentValue={scheduled}
        transferWindow={transferWindow}
        isSaving={isSaving}
      />

      <ConfirmationModal
        variant="v2"
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={async () => {
          const ok = await onDelete?.();
          if (ok !== false) setShowDelete(false);
        }}
        title="Delete this draft?"
        message={
          title
            ? `The draft ${title} and its publish date are removed.`
            : "The draft and its publish date are removed."
        }
        lines={["This can not be undone."]}
        confirmText="Delete draft"
        confirmButtonClass="bg-[#FF4D4F] hover:bg-red-600"
        loading={isSaving}
      />
    </>
  );
};

export default RequestDraftActions;
